/**
 * GEOMETRY FOR THE 2D RENDERER — computed from declared principal dimensions, and honest about
 * where that stops.
 *
 * ── WHAT THE API GIVES AND WHAT IT DOES NOT ───────────────────────────────────────────────────
 * `/api/fleet` declares each hull's principal dimensions: LBP, breadth, depth, Cb. Those are real
 * declared data and a midship section outline follows from them by arithmetic, not by guesswork.
 *
 * `/api/provenance` and `/api/atlas/scope` name the structural members — keel plate, side shell,
 * tank top, girders — but carry `solids` references for the 3D scene rather than 2D polylines.
 * **There is no endpoint that returns the detailed structural geometry as coordinates.**
 *
 * ── SO THIS FILE COMPUTES ONE THING AND REFUSES ANOTHER ───────────────────────────────────────
 * It builds the SECTION ENVELOPE and the frame stations, which are entailed by the declared
 * dimensions. It does not draw floors, girders, brackets or a bilge radius, because those are not
 * declared anywhere it can read — and a plausible-looking floor drawn at an invented height is
 * exactly the failure FP-018 exists to stop. A candidate cannot tell an invented member from a
 * declared one by looking, which is precisely why the renderer must not offer him the choice.
 *
 * Every function here returns members ALONGSIDE a provenance envelope describing what is missing, so
 * the surface can state the limit rather than quietly present a sketch as a drawing.
 */

import { refused } from '../../../packages/ankr-provenance/src/provenance.mjs';

/**
 * The midship section envelope: baseline, side shell to depth, deck.
 * Everything here is entailed by B and D. Nothing is shaped or eyeballed.
 */
export function sectionFromDimensions(ship) {
  const B = Number(ship?.B), D = Number(ship?.D);
  // Number(null) is 0 and finite — so absence is tested by name, not by isFinite alone.
  if (!Number.isFinite(B) || !Number.isFinite(D) || B <= 0 || D <= 0)
    return { members: [], note: refused('this vessel declares no breadth or depth') };

  const half = B / 2;
  return {
    members: [
      { id: 'baseline', label: 'Base line', layer: 'shell', weight: 1.8,
        points: [{ y: -half, z: 0 }, { y: half, z: 0 }] },
      { id: 'shell_port', label: 'Side shell (port)', layer: 'shell', weight: 1.8,
        points: [{ y: -half, z: 0 }, { y: -half, z: D }] },
      { id: 'shell_stbd', label: 'Side shell (stbd)', layer: 'shell', weight: 1.8,
        points: [{ y: half, z: 0 }, { y: half, z: D }] },
      { id: 'deck', label: 'Main deck', layer: 'decks', weight: 1.4,
        points: [{ y: -half, z: D }, { y: half, z: D }] },
      { id: 'centreline', label: 'Centre line', layer: 'frames', weight: 0.6,
        points: [{ y: 0, z: 0 }, { y: 0, z: D }] },
    ],
    note: refused(
      'floors, girders, the bilge radius and the tank top are not drawn — no endpoint returns their ' +
      'coordinates, and drawing them at invented positions would be indistinguishable from real ones'),
  };
}

/**
 * The profile: length and depth, with frame stations at the declared spacing.
 *
 * Frame spacing is NOT declared by the API. Rather than assume a value — and rather than quietly
 * omit the stations, which would make the drawing look complete when it is not — the stations are
 * computed only when a spacing is supplied by the caller, and refused otherwise.
 */
export function profileFromDimensions(ship, { frameSpacing = null } = {}) {
  const L = Number(ship?.LBP), D = Number(ship?.D);
  if (!Number.isFinite(L) || !Number.isFinite(D) || L <= 0 || D <= 0)
    return { members: [], note: refused('this vessel declares no length or depth') };

  const members = [
    { id: 'keel', label: 'Keel line', layer: 'shell', weight: 1.8,
      points: [{ x: 0, z: 0 }, { x: L, z: 0 }] },
    { id: 'deck_line', label: 'Deck line', layer: 'decks', weight: 1.4,
      points: [{ x: 0, z: D }, { x: L, z: D }] },
    { id: 'ap', label: 'AP', layer: 'frames', weight: 1.2, points: [{ x: 0, z: 0 }, { x: 0, z: D }] },
    { id: 'fp', label: 'FP', layer: 'frames', weight: 1.2, points: [{ x: L, z: 0 }, { x: L, z: D }] },
    { id: 'midship', label: '⊗ midships', layer: 'frames', weight: 1,
      points: [{ x: L / 2, z: 0 }, { x: L / 2, z: D }] },
  ];

  if (!frameSpacing)
    return { members, note: refused(
      'frame stations are not shown — frame spacing is not declared for this vessel, and a spacing ' +
      'assumed to make the drawing look finished would be a number a candidate could not check') };

  for (let x = frameSpacing, n = 1; x < L; x += frameSpacing, n++)
    members.push({ id: `fr${n}`, label: `Fr ${n}`, layer: 'frames', weight: 0.6,
                   points: [{ x, z: 0 }, { x, z: D }] });
  return { members, note: null };
}
